import React, { useEffect, useRef, useState } from 'react';
import CardStoreList from './CardStoreList';
import { useNavigate } from 'react-router-dom';
import { useLayoutContext } from '../../Contexts/LayoutContext';
import ButtonPrimary from '../Form/ButtonPrimary';
import { TbCurrentLocation, TbMoodConfuzed, TbX, TbZoom } from 'react-icons/tb';
import StoreService, { StoreListProps } from '../../Services/StoreService';
import { useOrderTypeContext } from '../../Contexts/OrderTypeContext';
import { ImSpinner8 } from 'react-icons/im';
import { useLoginContext } from '../../Contexts/LoginContext';
import { IMaskInput } from 'react-imask';
import { useStoreContext } from '../../Contexts/StoreContext';
import { orderTypeAsSlug } from '../../Utils/Helpers';
import Confirm from './Confirm';
import Modal from './Modal';
import { useCartHook } from '../../Hooks/CartHook';
import { useCartContext } from '../../Contexts/CartContext';
import { ORDER_TYPE_STORE_COUNTER } from '../../Utils/Constants/Service';
import HeaderLogo from './HeaderLogo';
import Button from '../Form/Button';
import { useStoreHook } from '../../Hooks/StoreHook';
import { useTranslation } from 'react-i18next';
import AddressService from '../../Services/AddressService';
import { ALL_STATES } from '../../Utils/Constants/Global';

const StoreList = () => {

    const { t } = useTranslation();
    const [stores, setStores] = useState<any>([]);
    const [storesFiltered, setStoresFiltered] = useState<any>([]);
    const [search, setSearch] = useState('');
    const navigate = useNavigate();
    const { orderType, getOrderTypeIdByName, visibleOrderType } = useOrderTypeContext();
    const { setMainLoading, setMainLoadingIcon, setMainLoadingIconNoSearchMap, setMainLoadingTitle, setMainLoadingMessage, setMainLoadingCloseButton, setMainLoadingIconError, company, states, setStates } = useLayoutContext();
    const { setGeolocationIsBlocked } = useLoginContext();
    const { currentStore, setCurrentStore } = useStoreContext();
    const [currentState, setCurrentState] = useState<string | null>(null);
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingStores, setLoadingStores] = useState(false);
    const [showConfirmChangeStore, setShowConfirmChangeStore] = useState(false);
    const [storeToSelect, setStoreToSelect] = useState<any>(null);
    const { currentCart, cartDisplay } = useCartContext();
    const [currentOrderType, setCurrentOrderType] = useState<any>(null);
    const { removeAll } = useCartHook();
    const { loadCompanyInfo } = useStoreHook();
    const isFirstRender = useRef(true);

    const resetCartItems = (store: any) => {
        return currentStore && currentCart?.items?.length > 0 && currentStore.id !== store.id;
    }

    const handleClickStore = (store: any) => {
        if (resetCartItems(store)) {
            setShowConfirmChangeStore(true);
            setStoreToSelect(store);
        } else {
            changeStore(store);
        }
    }

    const changeStore = (store: any) => {
        if (store.slug) {
            setCurrentStore(store);
            setStoreToSelect(null);
            if (resetCartItems(store)) {
                const payload = {
                    order_type: Number(getOrderTypeIdByName(orderType))
                }
                removeAll(currentCart.cart_id, payload);
            }
            navigateToStore(store);
        } else {
            setMainLoading(true);
            setMainLoadingIcon(false);
            setMainLoadingIconError(true);
            setMainLoadingTitle(t('layout.store_without_slug'));
            setMainLoadingMessage('');
            setMainLoadingCloseButton(true);
        }
    }

    const navigateToStore = (store: any) => {
        navigate(`/${orderTypeAsSlug(orderType)}/${store.slug}`);
    }

    const handleGetLocation = () => {
        setCurrentState('');
        setSearch('');
        setCurrentOrderType(null);

        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const { latitude, longitude } = position.coords;
              loadStoresList({ latitude, longitude });
              setGeolocationIsBlocked(false);
              addressByLatLng(`${latitude},${longitude}`);
            },
            (error) => {
                setGeolocationIsBlocked(true)
                setTimeout(() => {
                    setMainLoading(true);
                    setMainLoadingIcon(false);
                    setMainLoadingIconNoSearchMap(true);
                    setMainLoadingTitle(t('layout.geolocation_permission.denied.title'));
                    setMainLoadingMessage(t('layout.geolocation_permission.denied.message'));
                    setMainLoadingCloseButton(true);
                    handleUniqueState()
                }, 250);
            },
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0,
            }
          );
        } else {
          console.error(t('layout.geolocation_permission.denied.not_supported_by_browser'));
        }
    };

    const addressByLatLng = async (text: string) => {
        try {
            const addressSevice = new AddressService(orderType);
            const getAddressByLatLng = await addressSevice.addressByLatLng(text);

            const { state } = getAddressByLatLng;
            if (state) {
                setCurrentState(state);
            }

        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        const normalize = (text: string = "") => 
            text.toLowerCase().replace(/\s+/g, " ").trim();
            const normalizedSearch = normalize(search);
            const filtered = stores.filter((store: any) => {
                //* Filtra por nome, endereço ou cidade
                const name = normalize(store.name);
                const address = normalize(store.address);
                const city = normalize(store.city);

                const matchesSearch =
                    name.includes(normalizedSearch) ||
                    address.includes(normalizedSearch) ||
                    city.includes(normalizedSearch);

                //* Verifica se a loja possui o serviço ativo para o tipo atual
                const hasOrderType =
                    currentOrderType ?
                        Array.isArray(store.store_services) &&
                        store.store_services.some(
                            (service: any) => service.order_type === currentOrderType?.id && service.active
                        )
                    : true;

                //* Retorna somente se ambos os critérios forem atendidos
                return matchesSearch && hasOrderType;
            });
        setStoresFiltered(filtered);
    }, [search, stores, currentOrderType]);


    useEffect(() => {

        document.title = t('layout.store_list.title');
        loadCompanyInfo();

        //* Redirecionando para home da loja atual (auto-atendimento não possui tela de lojas)
        if (orderType === ORDER_TYPE_STORE_COUNTER && currentStore && cartDisplay) {
            navigate(`/${orderTypeAsSlug(orderType)}/${currentStore.slug}`);
        }

        const loadStates = async () => {
            setLoadingStates(true);
            try {
                const storeService = new StoreService(orderType);
                const states = await storeService.states();
                setStates(states);
            } catch (error) {
                setStates(ALL_STATES);
            } finally {
                setLoadingStates(false);
                handleGetLocation();
            }
        }

        if (states.length === 0) {
            loadStates();
        }
        setStates(ALL_STATES);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClickState = (state: string) => {
        loadStoresList({state});
        setCurrentState(state);
    }

    const handleUniqueState = () => {
        if (states.length > 0) {
            handleClickState(states[0]);
            setCurrentState(states[0]);
        }
    }

    const loadStoresList = async (params: StoreListProps) => {

        if (isFirstRender.current) {
            isFirstRender.current = false;
        }

        setLoadingStores(true);

        try {
            const storeService = new StoreService(orderType);
            const payload = {
                state: params.state?.toLowerCase(),
                latitude: params.latitude,
                longitude: params.longitude,
            }
            const stores = await storeService.stores(payload);

            //* storeCounter selecionado com apenas uma loja encontrada
            const isSingleStoreCounterOrder = orderType && orderType === ORDER_TYPE_STORE_COUNTER && stores && stores.length === 1 && stores[0].slug

            if (isSingleStoreCounterOrder) {
                setCurrentStore(stores[0]);
                navigate(`/${orderTypeAsSlug(orderType)}/${stores[0].slug}`);
                return;
            }

            setStores(stores);
            setStoresFiltered(stores);

        } catch (error) {
            setStores([]);
        } finally {
            setLoadingStores(false);
        }
    }

    const handleCurrentOrderType = (service: any) => {
        setCurrentOrderType(service);
    }

    const getMessageNotFound = () => {
        const hasState = Boolean(currentState);
        const hasSearch = Boolean(search && search.length > 0);
        const hasOrderType = Boolean(currentOrderType);

        const stateText = hasState ? (
            <> {t('layout.store_list.not_found_message.on_state')} <strong>{currentState}</strong></>
        ) : null;

        const searchText = hasSearch ? (
            <> {t('layout.store_list.not_found_message.with_research')} "<strong>{search}</strong>"</>
        ) : null;

        const orderTypeText = hasOrderType ? (
            <>
                {(hasState || hasSearch) && " e "}
                <> {t('layout.store_list.not_found_message.with_service')} <strong>{currentOrderType.label.toLowerCase()}</strong></>
            </>
        ) : null;

        return (
            <>
                {t('layout.store_list.no_stores_found')}
                {stateText}
                {searchText}
                {orderTypeText}
            </>
        );
    };

    return (
        <>
            <div className='flex flex-col gap-6 h-full w-full'>

                <section className='flex flex-col gap-6 w-full items-center justify-center'>

                    {(!currentStore && company) && (
                        <div className='flex justify-center text-center w-full mt-4'>
                            <HeaderLogo className='w-40 md:w-48 justify-center' />
                        </div>
                    )}

                    <div className='grid gap-1.5 text-center p-4 px-6'>
                        <h1 className='text-2xl font-bold leading-none text-shopfood-title'>{t('layout.store_list.title')}</h1>
                        <span className='text-base leading-none font-medium'>{t('layout.store_list.subtitle')}</span>
                    </div>

                    <div className='flex flex-col md:flex-row gap-4 w-full md:max-w-3xl'>
                        <div className='flex gap-1 w-full'>
                            <select
                                name="state"
                                disabled={loadingStates || loadingStores || states.length === 0}
                                value={currentState || ''}
                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                    handleClickState(e.target.value)
                                }
                                className="
                                    min-h-full w-auto rounded-lg rounded-r-none -mr-0.5
                                    p-2.5 py-3 pr-5 ring-2 ring-gray-200 bg-white rounded h-full font-bold
                                    disabled:bg-gray-100"
                                >
                                    <option value="">---</option>
                                    {states.map((uf: string) => (
                                        <option key={uf} value={uf}>{uf}</option>
                                    ))}
                            </select>

                            <div className="relative w-full">
                                <TbZoom size={20} className="pointer-events-none absolute left-3 top-3.5 h-5 w-5 text-gray-300 z-10" aria-hidden="true"/>
                                <div className='relative flex items-center justify-between w-full'>
                                    <IMaskInput
                                        id="first_name"
                                        mask={/^[a-zA-ZÀ-ÿ0-9\s]*$/}
                                        radix=""
                                        autoComplete="off"
                                        value={search}
                                        onAccept={(value: string) => setSearch(value)}
                                        unmask={true} // true|false|'typed'
                                        placeholder={loadingStores ? t('layout.store_list.search_placeholder_loading') : t('layout.store_list.search_placeholder')}
                                        disabled={loadingStores || (stores && stores.length === 0)}
                                        className='h-12 w-full ring-2 ring-gray-200 rounded-lg rounded-l-none bg-transparent pl-10 font-bold placeholder:font-normal text-shopfood-title placeholder:text-gray-400 focus:ring-gray-400 disabled:bg-gray-100'
                                    />
                                    {search.length > 0 && <TbX size={22} onClick={() => setSearch('')} className='absolute cursor-pointer p-1 right-2 hover:bg-gray-200 rounded-full transition-all duration-200' />}
                                </div>
                            </div>
                        </div>

                        <div className='flex w-full md:w-auto justify-center'>
                            <ButtonPrimary onClick={() => handleGetLocation()} className='w-full lg:w-auto items-center justify-center gap-1 text-nowrap'>
                                <span><TbCurrentLocation size={20} /></span>
                                <span>{t('layout.geolocation_permission.button_use_geolocation_label')}</span>
                            </ButtonPrimary>
                        </div>
                    </div>
                </section>


                {visibleOrderType.length > 0 &&
                    <section className="flex items-center justify-center w-full">
                        <div className="flex flex-wrap w-auto gap-1 text-sm md:text-base">

                            <Button onClick={() => setCurrentOrderType(null)} className={`ring-transparent hover:ring-transparent ${!currentOrderType ? 'pointer-events-none text-custom-primary bg-white active:bg-white hover:bg-white' : 'bg-gray-50'}`}>
                                {t('layout.store_list.filter_all')}
                            </Button>

                            {visibleOrderType.map((service: any, index: number) => {
                                const Icon = service.icon;
                                return (
                                    <Button
                                        key={index}
                                        onClick={() => handleCurrentOrderType(service)}
                                        className={`text-nowrap gap-1 ring-transparent hover:ring-transparent
                                            ${service.name === currentOrderType?.name ? 'pointer-events-none text-custom-primary bg-white active:bg-white hover:bg-white' : 'bg-gray-50'}
                                        `}
                                        >
                                        <div><Icon size={20} /></div>
                                        <span>{service.label}</span>
                                    </Button>
                                )
                            })}

                        </div>
                    </section>
                }

                {loadingStores && (
                    <div className='flex gap-1 justify-center items-center text-gray-500'>
                        <ImSpinner8 className="animate-spin" />
                        {t('layout.store_list.loading')}
                    </div>
                )}

                {!isFirstRender.current && !loadingStores && storesFiltered.length === 0 && (
                    <div className='flex flex-col px-8 gap-2 justify-center items-center text-center leading-none'>
                        <TbMoodConfuzed size={30} />
                        <span>{ getMessageNotFound() }</span>
                    </div>
                )}

                {(!loadingStores && stores) && (
                    <section className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 xl:gap-5 transition-width ease-in-out duration-200'>
                        {storesFiltered.map((store: any) =>
                            <CardStoreList isCollapsed enableCardClick={false} showServices onCardClick={() => handleClickStore(store)} store={store} key={`${store.id}`} />
                        )}
                    </section>
                )}
            </div>

            {(currentStore && storeToSelect) &&
                <Modal open={showConfirmChangeStore} className="max-w-sm m-4" onClose={() => setShowConfirmChangeStore(false)}>
                    <Confirm
                        title={t('layout.store_list.confirm_change_title')}
                        message={t('layout.store_list.confirm_change_message', {currentStore: currentStore?.name, newStore: storeToSelect?.name})}
                        labelConfirm={t('layout.store_list.confirm_button_label')}
                        onConfirm={() => {
                            changeStore(storeToSelect);
                        }}
                        onCancel={() => {
                            setShowConfirmChangeStore(false);
                        }}
                    />
                </Modal>
            }
        </>
    );
}

export default StoreList;
